import { Alert, Card, Flex, Layout, Result, Spin, Tag, Typography } from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"

import {
  hasCertificate,
  isClosedStatus
} from "../../components/Esign/signing/types.ts"
import type { EnvelopeStatus } from "../Esign/types/esign.types.ts"
import { openSigningLink } from "./api/signing.services"
import ConsentStep from "./components/ConsentStep"
import ConsumerRights from "./components/ConsumerRights"
import DocumentStep from "./components/DocumentStep"
import IdentityStep from "./components/IdentityStep"
import RefusedCard from "./components/RefusedCard"
import { useSigningDocument } from "./hooks/use-signing-document"
import type {
  OpenedLink,
  SigningRefusal,
  VerifyResult
} from "./types/signing.types"

const { Title, Text } = Typography

type Stage =
  | "opening"
  | "code"
  | "consent"
  | "document"
  | "declined"
  | "refused"

/**
 * The page a recipient with no EZBuild account lands on.
 *
 * The token arrives in the URL fragment and is read once, synchronously, then
 * wiped from the address bar: a fragment never reaches a server, but it does sit
 * in history and in anything that reports the current URL.
 *
 * The session it is exchanged for is held in memory only. Putting it in storage
 * would leave a working key to somebody's contract on a shared computer long
 * after they had walked away.
 */
// Consent precedes a signing field, so anyone who signs nothing and a closed envelope skip it.
const stageAfterIdentity = (
  role: string | undefined,
  status: EnvelopeStatus | undefined
): "consent" | "document" =>
  isClosedStatus(status) || role !== "signer" ? "document" : "consent"

const SigningPage: React.FC = () => {
  const { t } = useTranslation()
  const [stage, setStage] = useState<Stage>("opening")
  const [opened, setOpened] = useState<OpenedLink | null>(null)
  const [session, setSession] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<SigningRefusal | null>(null)
  const [maySign, setMaySign] = useState(true)
  const [completed, setCompleted] = useState(false)
  // The fragment, watched rather than read once. Opening a second link in the
  // same tab changes nothing but the hash, so the route never changes and the
  // page would otherwise keep showing the first recipient's document — and
  // their session with it.
  const { hash } = useLocation()
  const opening = useRef<string | null>(null)

  // Nothing this page requests may carry where it came from. The fragment is
  // already stripped from the address bar below, but a referrer would still name
  // /sign to every font, script and image host the browser talks to, and for a
  // page somebody was sent privately that is one leak too many.
  useEffect(() => {
    const meta = document.createElement("meta")
    meta.name = "referrer"
    meta.content = "no-referrer"
    document.head.appendChild(meta)
    return () => {
      document.head.removeChild(meta)
    }
  }, [])

  useEffect(() => {
    const token = hash.replace(/^#/, "")

    // Strict mode mounts twice in development, and clearing the fragment below
    // re-runs this effect; opening a link twice would record two openings for
    // one person walking in once.
    if (!token || opening.current === token) {
      if (!token && opening.current === null) {
        setRefusal({ reason: "unknown" })
        setStage("refused")
      }
      return
    }
    opening.current = token
    setStage("opening")
    setOpened(null)
    setSession(null)
    setMaySign(true)
    setCompleted(false)
    setAgreedAtConsent(false)
    window.history.replaceState(null, "", window.location.pathname)

    void openSigningLink(token)
      .then((response) => {
        if (!response.ok || !response.data) {
          setRefusal(response.refusal)
          setStage("refused")
          return
        }
        const { data } = response
        setOpened(data)
        setCompleted(hasCertificate(data.envelope_status))
        setSession(data.session)
        if (data.next === "code") {
          setStage("code")
          return
        }
        // No code asked: the link alone identifies them, so straight to what comes after it.
        if (isClosedStatus(data.envelope_status)) setMaySign(false)
        setStage(stageAfterIdentity(data.recipient.role, data.envelope_status))
      })
      // A network that never answers would otherwise leave the spinner turning
      // with nothing to read and nothing to try.
      .catch(() => {
        setRefusal({ reason: "unknown" })
        setStage("refused")
      })
  }, [hash])

  const onIdentified = useCallback(
    (verified: VerifyResult) => {
      setSession(verified.session)
      const status = verified.envelope_status ?? opened?.envelope_status
      const closed = isClosedStatus(status)
      setCompleted(hasCertificate(status))
      if (closed) setMaySign(false)
      setStage(stageAfterIdentity(opened?.recipient.role, status))
    },
    [opened]
  )

  const onDeclined = useCallback(() => setStage("declined"), [])

  const onRefused = useCallback((next: SigningRefusal) => {
    setRefusal(next)
    setStage("refused")
  }, [])

  // Fetched here, not per step: every fetch is an audited opening.
  const wantsDocument = stage === "consent" || stage === "document"
  // A signer past consent is handed the held blob, so only a viewer still needs the URL.
  const isSigner = opened?.recipient.role === "signer"
  const pdf = useSigningDocument(
    session,
    stage === "consent" || (stage === "document" && !isSigner)
  )

  const documentReady = pdf.url !== null || pdf.refusal !== null
  const isSignerLink = opened?.recipient.role === "signer"
  // Reading and signing a page needs width; the steps around it read better narrow.
  const frameWidth =
    stage === "document" ? 960 : stage === "consent" ? 920 : 640

  // Moving on before the preview lands would make the signing screen fetch it again.
  const [agreedAtConsent, setAgreedAtConsent] = useState(false)
  const onAgreed = useCallback(() => setAgreedAtConsent(true), [])
  useEffect(() => {
    if (stage === "consent" && agreedAtConsent && documentReady) {
      setAgreedAtConsent(false)
      setStage("document")
    }
  }, [stage, agreedAtConsent, documentReady])

  useEffect(() => {
    if (pdf.refusal) onRefused(pdf.refusal)
  }, [pdf.refusal, onRefused])

  return (
    <Layout className="min-h-svh auth-bg">
      <Flex
        vertical
        align="center"
        justify="center"
        className="min-h-svh"
        style={{ padding: "16px" }}
      >
        <Flex
          align="center"
          justify="space-between"
          style={{ width: "100%", maxWidth: frameWidth, marginBottom: 12 }}
        >
          <Text strong style={{ fontSize: 18 }}>
            EZ-Sign
          </Text>
          <Text type="secondary" className="text-xs">
            {t("signing.poweredBy")}
          </Text>
        </Flex>
        <div
          className={
            stage === "consent" && isSignerLink
              ? "grid w-full grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_18rem]"
              : "w-full"
          }
          style={{ maxWidth: frameWidth }}
        >
          <Card style={{ width: "100%" }}>
            {opened && stage !== "refused" && (
              <Flex vertical gap={4} style={{ marginBottom: 24 }}>
                {isSignerLink && stage !== "declined" && (
                  <Tag color="processing" className="!m-0 w-fit">
                    {t("signing.requested")}
                  </Tag>
                )}
                <Title level={4} style={{ margin: 0 }}>
                  {opened.document_name}
                </Title>
                <Text type="secondary">
                  {t("signing.addressedTo", { name: opened.recipient.name })}
                </Text>
              </Flex>
            )}

            {stage === "opening" && (
              <Flex justify="center" style={{ padding: "48px 0" }}>
                <Spin size="large" />
              </Flex>
            )}

            {stage === "code" && session && (
              <IdentityStep
                session={session}
                onIdentified={onIdentified}
                onRefused={onRefused}
              />
            )}

            {stage === "consent" && session && opened && (
              <ConsentStep
                session={session}
                documentName={opened.document_name}
                documentUrl={pdf.url}
                documentBlob={pdf.blob}
                documentReady={documentReady}
                onAgreed={onAgreed}
                onDeclined={onDeclined}
                onRefused={onRefused}
              />
            )}

            {stage === "document" && session && opened && (
              <DocumentStep
                session={session}
                documentUrl={pdf.url}
                documentBlob={pdf.blob}
                documentName={opened.document_name}
                recipientName={opened.recipient.name}
                isSigner={opened.recipient.role === "signer"}
                completed={completed}
                onMaySign={setMaySign}
                onDeclined={onDeclined}
                onRefused={onRefused}
              />
            )}

            {stage === "declined" && (
              <Result
                status="info"
                title={t("signing.decline.declinedTitle")}
                subTitle={t("signing.decline.declinedBody")}
              />
            )}

            {wantsDocument && session && opened && (
              <ConsumerRights
                session={session}
                isSigner={opened.recipient.role === "signer"}
                canWithdraw={maySign}
                onWithdrawn={onRefused}
              />
            )}

            {stage === "refused" && <RefusedCard refusal={refusal} />}
          </Card>
          {stage === "consent" && isSignerLink && (
            <Card size="small" title={t("signing.nextTitle")}>
              <Flex vertical gap={8}>
                <ol className="m-0 list-decimal pl-5">
                  {["1", "2", "3"].map((step) => (
                    <li key={step}>
                      <Text>{t(`signing.nextStep.${step}`)}</Text>
                    </li>
                  ))}
                </ol>
                <Text type="secondary" className="text-xs">
                  {t("signing.notSubmitted")}
                </Text>
              </Flex>
            </Card>
          )}
        </div>

        {stage !== "refused" && (
          <Alert
            type="info"
            showIcon
            style={{ width: "100%", maxWidth: frameWidth, marginTop: 16 }}
            message={t(
              opened?.next === "code"
                ? "signing.linkIsPersonal"
                : "signing.linkIsPersonalNoCode"
            )}
          />
        )}
      </Flex>
    </Layout>
  )
}

export default SigningPage
